<template>
	<div>
		<v-dialog
			v-model="resultsBoxVisible"
			:width="$vuetify.display.smAndDown ? '95%' : '1000px'"
			:height="$vuetify.display.smAndDown ? '95%' : '80vh'"
			@click:outside="closeDialog"
			@keydown.esc="closeDialog"
			scrollable
		>
			<v-card>
				<v-card-title class="dialog-header sgnl-subtitle-medium sgnl-gap-8">
					Search Results
					<v-spacer />
					<template v-if="githubStore.githubLoggedIn">
						<v-btn
							variant="outlined"
							size="small"
						>
							<span v-if="!$vuetify.display.smAndDown">
								{{ githubStore.githubUsername }}
							</span>
							<v-avatar size="20">
								<v-img
									v-if="githubStore.githubUserDetails"
									:src="githubStore.githubUserDetails.avatar_url"
									:alt="githubStore.githubUsername"
									cover
								/>
							</v-avatar>
						</v-btn>
					</template>
					<template v-else>
						<v-btn
							v-if="!$vuetify.display.mobile"
							variant="flat"
							color="primary"
							@click="$emit('github-login')"
						>
							<LogoGithub16 />
							Login with GitHub
						</v-btn>
					</template>
					<v-btn
						icon
						variant="text"
						size="small"
						@click="closeDialog"
						data-test="github-search-results-dialog-close-button"
					>
						<Close16 />
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="dialog-body">
					<RepoCardList
						:displayRepoList="displayRepoList"
						:showNoResultsBanner="showNoResultsBanner"
						noResultsText="No repositories matching the query were found."
						:loadingRepos="loadingRepos"
						:endOfRepoList="endOfRepoList"
						skeletonKeyPrefix="search-skeleton"
						@repo-connection-request="requestRepoConnect"
						@repository-disconnected="repoDisconnectionEventHandler"
						@load-next-page="loadNextPage"
					/>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import LogoGithub16 from "@carbon/icons-vue/es/logo--github/16";
import RepoCardList from "@/components/Repositories/RepoCardList.vue";
import { useGithubStore } from "@/stores/github";
import type { Endpoints } from "@octokit/types";
import { RepoConnectionConfig } from "@/types/general";
import { signaloidApiClient } from "@/js/signaloidClient";

type ComponentData = {
	fullRepoList: Endpoints["GET /user/repos"]["response"]["data"];
	displayRepoList: Endpoints["GET /user/repos"]["response"]["data"];
	repositoriesPerPage: number;
	repositoriesPage: number;
	loadingRepos: boolean;
	resultsBoxVisible: boolean;
	endOfRepoList: boolean;
};

export default defineComponent({
	name: "SearchGithubReposDialog",
	components: { RepoCardList, Close16, LogoGithub16 },
	props: {
		dialogVisible: Boolean,
		searchQuery: {
			type: String,
			required: true,
		},
	},
	data: (): ComponentData => ({
		repositoriesPerPage: 20,
		repositoriesPage: 0,
		fullRepoList: [],
		displayRepoList: [],
		loadingRepos: true,
		resultsBoxVisible: false,
		endOfRepoList: false,
	}),
	emits: {
		"close-dialog": () => true,
		"github-login": () => true,
		"repo-connection-request": (_data: RepoConnectionConfig) => true,
		"repository-disconnected": (_data: { repositoryID: string }) => true,
	},
	setup() {
		const githubStore = useGithubStore();
		return { githubStore };
	},
	methods: {
		searchGithub() {
			this.resultsBoxVisible = true;
			this.repositoriesPage = 0;
			this.endOfRepoList = false;
			this.fullRepoList = [];
			this.displayRepoList = [];
			this.loadNextPage();
		},

		loadNextPage() {
			this.loadingRepos = true;
			this.fetchNextRepositoryBatch().then(() => {
				this.loadingRepos = false;
				this.displayRepoList = this.fullRepoList;
			});
		},
		fetchNextRepositoryBatch() {
			this.repositoriesPage += 1;
			return signaloidApiClient
				.get(
					`proxy/github/search/repositories?q=${this.searchQuery}&per_page=${this.repositoriesPerPage}&page=${this.repositoriesPage}`,
				)
				.then((response) => {
					if (response.data.items.length < this.repositoriesPerPage) {
						this.endOfRepoList = true;
					}
					this.fullRepoList = [...this.fullRepoList, ...response.data.items];
				})
				.catch((error) => console.log(error));
		},
		closeDialog() {
			this.$emit("close-dialog");
		},
		requestRepoConnect(data: RepoConnectionConfig) {
			this.$emit("repo-connection-request", data);
		},
		repoDisconnectionEventHandler(data) {
			this.$emit("repository-disconnected", data);
		},
	},
	computed: {
		showNoResultsBanner(): boolean {
			return !this.loadingRepos && this.displayRepoList.length == 0;
		},
	},
	mounted() {
		this.searchGithub();
	},
});
</script>
